import PeopleIcon from '@mui/icons-material/People';
import ArchiveIcon from '@mui/icons-material/Archive';
import {
  Avatar,
  Box,
  Divider,
  Drawer,
  DrawerProps,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Typography,
} from '@mui/material';
import classNames from 'classnames';
import { graphql, Link, useStaticQuery } from 'gatsby';
import React, { Fragment } from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import DiscordIcon from '../../assets/svg/DiscordIcon';
import Logo from '../../assets/svg/Logo';
import { getInviteLink, getOnlineMembers } from '../../state/discord';
import sections, { Difficulties, Group } from '../../utils/sections';
import Label from '../Label';
import ExpandableMenuItem from './ExpandableMenuItem';
import DiscretizeLink from './Link';
import MenuItem from './MenuItem';
import { Location } from '../Layout';

const useStyles = makeStyles()((theme) => ({
  drawer: {
    width: 240,
    borderRight: 'none',
    backgroundColor: 'transparent',
    boxShadow: 'none',
    backgroundImage: 'unset',
  },
  list: {
    backgroundColor: theme.palette.background.paper,
  },
  gradient: {
    marginTop: -2,
    flexGrow: 1,
    background: `linear-gradient(to bottom, ${theme.palette.background.paper} 0%, rgba(0,0,0,0) 20%, rgba(0,0,0,0) 100%)`,
    borderTopWidth: 0,
    borderLeftWidth: 0,
    borderBottomWidth: 0,
    borderRightWidth: 2,
    borderRightStyle: 'solid',
    borderImage: `linear-gradient(to bottom, ${theme.palette.divider} 0%, rgba(0,0,0,0) 10%, rgba(0,0,0,0) 100%) 2 100%`,
  },
  listItem: {
    borderRight: `2px solid ${theme.palette.divider}`,
  },
  logoWrapper: {
    padding: theme.spacing(2),
  },
  link: {
    color: theme.palette.common.white,
    transition: theme.transitions.create(['color', 'border-right'], {
      duration: theme.transitions.duration.short,
    }),
    borderRight: `2px solid transparent`,
    '&:hover': {
      borderRight: `2px solid ${theme.palette.primary.dark}`,
    },
  },
  nested: {
    paddingLeft: `${theme.spacing(2)} !important`,
  },
  listSubheader: {
    lineHeight: `32px`,
    fontStyle: 'italic',
    '&:not(*:first-of-type)': {
      marginTop: theme.spacing(1),
    },
  },
  icon: {
    fontSize: 'inherit',
    transition: theme.transitions.create(['transform'], {
      duration: theme.transitions.duration.short,
    }),
  },
  discordText: {
    display: 'flex',
    alignItems: 'center',
  },
  discordIcon: {
    marginRight: 4,
  },
  discordLabel: {
    display: 'inline-flex',
    alignItems: 'center',
  },
  discordPeopleIcon: {
    marginRight: theme.spacing(0.25),
    width: '0.6em',
    height: '0.6em',
  },
  miscLinks: {
    fontFamily: theme.typography.fontFamily,
    fontWeight: theme.typography.body1.fontWeight,
    fontSize: theme.typography.pxToRem(12),
    color: theme.palette.text.secondary,
    '& > a': {
      color: 'inherit',
      '&:hover': {
        color: theme.palette.primary.dark,
      },
    },
  },
}));

interface NavigationProps {
  className?: string;
  open?: boolean;
  onClose?: ({ force }: { force: boolean }) => void;
  variant: DrawerProps['variant'];
  location: Location;
}

function Navigation({
  className,
  variant,
  open = false,
  onClose,
  location: { pathname },
}: NavigationProps) {
  const { classes } = useStyles();

  const discordInvite = useSelector(getInviteLink);
  const discordOnline = useSelector(getOnlineMembers);

  const data = useStaticQuery<Queries.NavigationComponentQuery>(graphql`
    query NavigationComponent {
      builds: allMdx(
        filter: {
          fields: { section: { eq: "builds" } }
          frontmatter: { hidden: { eq: false }, archive: { eq: false } }
        }
      ) {
        group(field: frontmatter___role) {
          fieldValue
          edges {
            node {
              ...BuildsSkeleton
            }
          }
        }
      }
      guides: allMdx(
        filter: {
          fields: { section: { eq: "guides" } }
          frontmatter: { hidden: { ne: true } }
        }
      ) {
        edges {
          node {
            ...GuidesSkeleton
          }
        }
      }
      fractals: allMdx(
        filter: {
          fields: { section: { eq: "fractals" } }
          frontmatter: { hidden: { ne: true } }
        }
      ) {
        edges {
          node {
            ...FractalsSkeleton
          }
        }
      }
    }
  `);

  return (
    <Drawer
      className={className}
      variant={variant}
      open={open}
      onClose={onClose}
      classes={{ paper: classes.drawer }}
    >
      <List component="nav" disablePadding className={classes.list}>
        <ListItem
          component={Link}
          to="/"
          className={classNames(classes.logoWrapper, classes.listItem)}
        >
          <Logo height={46} />
        </ListItem>

        <Divider />

        <MenuItem
          pathname={pathname}
          to="/guides/new-player-guide/"
          label="New Player Guide"
        />

        <Divider />

        {sections
          .filter(
            ({ path }) =>
              data[path.substring(1) as 'builds' | 'guides' | 'fractals'],
          )
          .map(
            ({ name, path, renderNavigationText, orderGroups, orderEdges }) => {
              const sectionData =
                data[path.substring(1) as 'builds' | 'guides' | 'fractals'];

              const mapEdges = ({
                node: {
                  frontmatter,
                  fields: { slug },
                },
              }: {
                node: {
                  frontmatter: {
                    api?: number;
                    title: string;
                    specialization: string;
                    difficulties: Difficulties[];
                  };
                  fields: {
                    slug: string;
                  };
                };
              }) => {
                const link = `${slug}`;

                return (
                  <MenuItem
                    key={link}
                    pathname={pathname}
                    to={link}
                    label={renderNavigationText(frontmatter)}
                    nested
                    onClick={() => {
                      if (onClose) {
                        onClose({ force: false });
                      }
                    }}
                    fractalApi={frontmatter.api}
                  />
                );
              };

              return (
                <ExpandableMenuItem
                  key={path}
                  pathname={pathname}
                  path={path}
                  label={name}
                  submenu={
                    <>
                      {path === '/guides' && (
                        <MenuItem
                          pathname={pathname}
                          to="https://fast.farming-community.eu/instances/fractals"
                          label={
                            <>
                              <Avatar
                                src="/fast.webp"
                                sx={{
                                  width: 14,
                                  height: 14,
                                  display: 'inline-flex',
                                }}
                              />{' '}
                              Fractal Gold per Hour
                              <Box display="flex" justifyContent="flex-end">
                                <Typography
                                  variant="caption"
                                  style={{ fontSize: '0.8em' }}
                                >
                                  by [fast] Farming
                                </Typography>
                              </Box>
                            </>
                          }
                          nested
                        />
                      )}
                      {
                        // @ts-ignore
                        sectionData?.group && orderGroups
                          ? // @ts-ignore
                            orderGroups(sectionData?.group).map((group) => (
                              <Fragment key={group.fieldValue}>
                                <ListSubheader
                                  component="div"
                                  disableSticky
                                  className={classes.listSubheader}
                                >
                                  <span className={classes.nested}>
                                    {`${group.fieldValue} ${name}`}
                                  </span>
                                </ListSubheader>
                                {
                                  // @ts-ignore
                                  orderEdges(group.edges).map(mapEdges)
                                }
                              </Fragment>
                            ))
                          : // @ts-ignore
                            orderEdges(sectionData?.edges).map(mapEdges)
                      }
                      {path === '/builds' && (
                        <MenuItem
                          pathname={pathname}
                          to="/builds/archive"
                          label={
                            <>
                              <ArchiveIcon style={{ fontSize: 16 }} /> Archived
                              builds
                            </>
                          }
                          nested
                        />
                      )}{' '}
                    </>
                  }
                />
              );
            },
          )}

        <Divider />

        <MenuItem
          pathname={pathname}
          to="/augmentations"
          label="Augmentations Calc"
        />
        {/*
        <ExpandableMenuItem
          pathname={pathname}
          path="/bounties"
          label="Bounties"
          submenu={
            <>
              <MenuItem
                pathname={pathname}
                to="/bounties/infos"
                label="Infos"
                nested
              />
              <MenuItem
                pathname={pathname}
                to="/bounties/leaderboards"
                label="Leaderboards"
                nested
              />
              <MenuItem
                pathname={pathname}
                to="/bounties/rules"
                label="Rules"
                nested
              />
              <MenuItem
                pathname={pathname}
                to="/bounties/submission"
                label="Submit"
                nested
              />
            </>
          }
        />
        */}
        <MenuItem
          pathname={pathname}
          to="https://optimizer.discretize.eu/"
          label="Gear Optimizer"
        />
        <MenuItem pathname={pathname} to="/apply" label="Apply to Discretize" />

        {discordInvite && (
          <ListItem
            button
            component={Link}
            to={discordInvite}
            className={classNames(classes.link, classes.listItem)}
          >
            <ListItemText
              primaryTypographyProps={{
                classes: { root: classes.discordText },
              }}
            >
              <DiscordIcon
                height={24}
                width={24}
                className={classes.discordIcon}
              />
              Discord
            </ListItemText>

            <Label className={classes.discordLabel}>
              <PeopleIcon className={classes.discordPeopleIcon} />
              {discordOnline}
            </Label>
          </ListItem>
        )}

        <Divider />

        <ListItem className={classes.listItem}>
          <ListItemText disableTypography className={classes.miscLinks}>
            <DiscretizeLink to="https://ublockorigin.com/">
              Ad-free version
            </DiscretizeLink>
          </ListItemText>
        </ListItem>

        <ListItem className={classes.listItem}>
          <ListItemText disableTypography className={classes.miscLinks}>
            <DiscretizeLink to="/bounties/">Bounties</DiscretizeLink> {' · '}
            <DiscretizeLink to="https://www.patreon.com/discretize">
              Patreon
            </DiscretizeLink>
            {' · '}
            <DiscretizeLink to="/legal">Legal</DiscretizeLink>
          </ListItemText>
        </ListItem>
      </List>

      <div className={classes.gradient} />
    </Drawer>
  );
}

export default Navigation;
